import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, AlertCircle, RefreshCw, Wrench, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityConfig = {
  critical: { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle, label: 'Critical' },
  high: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertCircle, label: 'High' },
  medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle, label: 'Medium' },
  low: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: CheckCircle, label: 'Low' },
};

const statusConfig = {
  critical: { bg: 'bg-red-500', text: 'Critical Issues' },
  warning: { bg: 'bg-orange-500', text: 'High Priority Issues' },
  attention: { bg: 'bg-yellow-500', text: 'Attention Needed' },
  healthy: { bg: 'bg-green-500', text: 'All Clear' },
};

export default function EdgeCaseAuditPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [fullScan, setFullScan] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedTypes, setExpandedTypes] = useState({});
  const [fixing, setFixing] = useState({});

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/edge-cases/summary`);
      const data = await res.json();
      if (data.success) {
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
      toast.error('Failed to load summary');
    }
  }, []);

  const fetchFullScan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/edge-cases/scan`);
      const data = await res.json();
      if (data.success) {
        setFullScan(data);
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Error fetching full scan:', err);
      toast.error('Failed to run full scan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary().finally(() => setLoading(false));
  }, [fetchSummary]);

  const toggleType = (type) => {
    setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleAutoFix = async (edgeCaseType, entityId) => {
    const key = `${edgeCaseType}-${entityId}`;
    setFixing(prev => ({ ...prev, [key]: true }));
    
    try {
      const res = await fetch(`${API_URL}/api/edge-cases/auto-fix/${edgeCaseType}/${entityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message || 'Auto-fix applied successfully');
        // Refresh data
        fetchFullScan();
      } else {
        toast.error(data.detail || 'Auto-fix failed');
      }
    } catch (err) {
      console.error('Auto-fix error:', err);
      toast.error('Failed to apply auto-fix');
    } finally {
      setFixing(prev => ({ ...prev, [key]: false }));
    }
  };

  const SeverityBadge = ({ severity }) => {
    const config = severityConfig[severity] || severityConfig.low;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const StatusIndicator = ({ status }) => {
    const config = statusConfig[status] || statusConfig.attention;
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium ${config.bg}`}>
        {status === 'healthy' ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {config.text}
      </div>
    );
  };

  const IssueCard = ({ issue }) => {
    const key = `${issue.edge_case_type}-${issue.entity_id}`;
    const isFixing = fixing[key];
    
    return (
      <Card className="mb-3 border-l-4" style={{ borderLeftColor: severityConfig[issue.severity]?.color.includes('red') ? '#ef4444' : severityConfig[issue.severity]?.color.includes('orange') ? '#f97316' : severityConfig[issue.severity]?.color.includes('yellow') ? '#eab308' : '#3b82f6' }}>
        <CardContent className="pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <SeverityBadge severity={issue.severity} />
                {issue.entity_reference && (
                  <Badge variant="outline" className="font-mono text-xs">
                    {issue.entity_reference}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-700 mb-2">{issue.description}</p>
              {issue.context && Object.keys(issue.context).length > 0 && (
                <div className="text-xs text-gray-500 space-y-1">
                  {Object.entries(issue.context).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <span className="font-medium capitalize">{k.replace(/_/g, ' ')}:</span>
                      <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {issue.auto_fixable ? (
                <Button
                  size="sm"
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleAutoFix(issue.edge_case_type, issue.entity_id)}
                  disabled={isFixing}
                >
                  {isFixing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Wrench className="h-4 w-4 mr-1" />
                      Auto-Fix
                    </>
                  )}
                </Button>
              ) : (
                <Badge variant="outline" className="text-xs">Manual Fix Required</Badge>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(`/fm/jobs/${issue.entity_id}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                View
              </Button>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-gray-600">
              <span className="font-medium">Next Action:</span> {issue.next_action}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-[#1F7A78]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edge Case Audit Engine</h1>
            <p className="text-sm text-gray-500 mt-1">
              Auto-detect stuck jobs, missing transitions, and broken workflows
            </p>
          </div>
          <div className="flex items-center gap-3">
            {summary && <StatusIndicator status={summary.status} />}
            <Button onClick={fetchFullScan} disabled={loading}>
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Run Full Scan
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-gray-900">{summary.total}</div>
              <div className="text-sm text-gray-500">Total Issues</div>
            </CardContent>
          </Card>
          <Card className={summary.critical > 0 ? 'ring-2 ring-red-500' : ''}>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-red-600">{summary.critical}</div>
              <div className="text-sm text-gray-500">Critical</div>
            </CardContent>
          </Card>
          <Card className={summary.high > 0 ? 'ring-2 ring-orange-500' : ''}>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{summary.high}</div>
              <div className="text-sm text-gray-500">High</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{summary.medium}</div>
              <div className="text-sm text-gray-500">Medium</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-green-600">{summary.auto_fixable || 0}</div>
              <div className="text-sm text-gray-500">Auto-Fixable</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="summary">By Category</TabsTrigger>
          <TabsTrigger value="all">All Issues</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          {summary?.by_type && Object.keys(summary.by_type).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(summary.by_type).map(([type, info]) => (
                <Card key={type} className={info.count > 0 ? 'border-l-4 border-l-orange-500' : ''}>
                  <Collapsible open={expandedTypes[type]} onOpenChange={() => toggleType(type)}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-base">{info.label}</CardTitle>
                            <SeverityBadge severity={info.severity} />
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={info.count > 0 ? 'destructive' : 'secondary'}>
                              {info.count} issues
                            </Badge>
                            {expandedTypes[type] ? (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent>
                        {fullScan?.by_type?.[type]?.length > 0 ? (
                          fullScan.by_type[type].map((issue, idx) => (
                            <IssueCard key={`${issue.entity_id}-${idx}`} issue={issue} />
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">
                            {info.count > 0 
                              ? 'Run full scan to see issue details' 
                              : 'No issues in this category'}
                          </p>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-700">All Systems Healthy</p>
                <p className="text-sm text-gray-500">No edge cases detected</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          {fullScan?.issues?.length > 0 ? (
            <div className="space-y-3">
              {fullScan.issues.map((issue, idx) => (
                <IssueCard key={`${issue.entity_id}-${idx}`} issue={issue} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-gray-500">Run a full scan to see all issues</p>
                <Button onClick={fetchFullScan} className="mt-4">
                  Run Full Scan
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Last Scan Time */}
      {fullScan?.scanned_at && (
        <p className="text-xs text-gray-400 mt-4 text-right">
          Last scan: {new Date(fullScan.scanned_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
